import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, CheckCircle, Lock, Clock, User, Stethoscope } from "lucide-react";
import { Badge } from "../../ui/badge";

const noteSections = [
  {
    title: "Chief Complaint",
    content: "Patient presents with sensitivity to cold on lower left quadrant, onset 2 weeks ago.",
  },
  {
    title: "Clinical Findings",
    content: "Tooth #19: recurrent decay distal surface. Percussion negative, vitality positive. BOP noted on #18-19.",
  },
  {
    title: "Treatment Rendered",
    content: "D2392 - Composite restoration, 2 surfaces (DO) on tooth #19. Local anesthesia: 2% Lidocaine 1:100k epi.",
  },
  {
    title: "Post-Op Instructions",
    content: "Avoid hard foods 24h. Mild sensitivity expected 1-2 weeks. OTC analgesics PRN. RTC 6mo recall.",
  },
];

const PMSClinicalNotesSlide = () => {
  const [typingSection, setTypingSection] = useState(-1);
  const [typedChars, setTypedChars] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setTypingSection(-1);
        setTypedChars(0);
        setIsTranscribing(false);
        await new Promise((r) => setTimeout(r, 2000));

        for (let s = 0; s < noteSections.length; s++) {
          setTypingSection(s);
          setIsTranscribing(true);
          const content = noteSections[s].content;
          for (let c = 0; c <= content.length; c += 3) {
            setTypedChars(Math.min(c, content.length));
            await new Promise((r) => setTimeout(r, 30));
          }
          setTypedChars(content.length);
          setIsTranscribing(false);
          await new Promise((r) => setTimeout(r, 800));
        }

        await new Promise((r) => setTimeout(r, 4000));
      }
    };
    cycle();
  }, []);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute top-[30%] right-[20%] w-[300px] h-[300px] bg-sky-100 rounded-full blur-[80px] opacity-40" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <FileText className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Powerful{" "}
              <span className="text-blue-600">Clinical Notes</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              HIPAA-compliant clinical documentation with smart templates,
              provider signatures, and secure audit trails. Structured sections
              keep notes consistent and complete across your entire practice.
            </p>
          </motion.div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              "Smart Templates",
              "HIPAA Secure",
              "Audit Trail",
              "Provider Signatures",
              "Structured Sections",
              "Custom Fields",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">Templates</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Pre-Built & Custom
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm"
            >
              <div className="flex items-center gap-1">
                <Lock className="w-5 h-5 text-blue-600" />
                <span className="text-2xl font-bold text-blue-600">HIPAA</span>
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Fully Compliant
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Side: Clinical Note Mockup */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Clinical Note</h3>
                  <p className="text-xs text-slate-500">Visit: Feb 15, 2026</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  HIPAA Secure
                </Badge>
              </div>
            </div>

            {/* Patient & Provider Info */}
            <div className="grid grid-cols-2 gap-3 p-4 border-b border-slate-100">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-3 h-3 text-blue-600" />
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Patient</p>
                </div>
                <p className="font-bold text-slate-900 text-sm">Jennifer Martinez</p>
                <p className="text-xs text-slate-600">DOB: 08/12/1988</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="flex items-center gap-2 mb-1">
                  <Stethoscope className="w-3 h-3 text-blue-600" />
                  <p className="text-[10px] text-slate-500 uppercase font-bold">Provider</p>
                </div>
                <p className="font-bold text-slate-900 text-sm">Dr. Smith, DDS</p>
                <p className="text-xs text-slate-600">License: CA-123456</p>
              </div>
            </div>

            {/* Note Sections */}
            <div className="flex-1 p-4 space-y-3 overflow-y-auto">
              {noteSections.map((section, i) => {
                const isActive = typingSection === i;
                const isCompleted = typingSection > i;
                const displayContent =
                  isActive
                    ? section.content.slice(0, typedChars)
                    : isCompleted
                    ? section.content
                    : "";

                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: 10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + i * 0.15, duration: 0.4 }}
                    className={`rounded-xl p-3 border-2 transition-all duration-300 ${
                      isActive
                        ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                        : isCompleted
                        ? "bg-blue-50/50 border-blue-200"
                        : "bg-slate-50 border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-bold text-slate-700">{section.title}</p>
                      {isCompleted && (
                        <CheckCircle className="w-3.5 h-3.5 text-blue-600" />
                      )}
                      {isActive && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-blue-500 font-bold">Filling in...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed min-h-[20px]">
                      {displayContent}
                      {isActive && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity }}
                          className="inline-block w-0.5 h-4 bg-blue-600 ml-0.5 align-middle"
                        />
                      )}
                    </p>
                  </motion.div>
                );
              })}
            </div>

            {/* Signature Section */}
            <div className="h-16 border-t border-slate-100 px-6 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-lg italic shadow-md">
                  DS
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-900">Dr. Sarah Smith</p>
                  <div className="flex items-center gap-1 text-[10px] text-slate-500">
                    <Clock className="w-3 h-3" />
                    <span>Pending Signature</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-600">Template-Powered Documentation</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSClinicalNotesSlide;
